import 'styles/eventIndicators.css'

import TFloatingPopover from 'components/popovers/TFloatingPopover'
import { useAppSettings } from 'components/providers/AppSettingsProvider'
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import LocalStorage from 'services/LocalStorage'
import Logger from 'services/Logger'
import { LocalStorageKey } from 'types/services/localStorage'

// Stable callbacks — never change reference after mount.
// Components that only call open/close should subscribe to this context
// so they are NOT re-rendered when a popup opens or closes.
type PopoverCallbacksType = {
  openPopover: (
    eventId: string,
    content: React.ReactNode,
    preventClose?: boolean,
    onOutsideClick?: () => void,
    elementPointTo?: HTMLElement
  ) => void
  closePopover: (withCache?: boolean) => void
  setPreventClose: (value: boolean) => void
  setOnOutsideClick: (callback: () => void) => void
  pointTo: (el: HTMLElement | null, eventId: string) => void
}

type PopoverContextType = PopoverCallbacksType & {
  currentEventId: string | null
}

// Stable context — never causes re-renders in subscribers after mount
const EventPopoverCallbacksContext = createContext<PopoverCallbacksType>({
  openPopover: () => {},
  closePopover: () => {},
  setPreventClose: () => {},
  setOnOutsideClick: () => {},
  pointTo: () => {}
})

// Reactive context — changes on every popup open/close
const EventPopoverContext = createContext<PopoverContextType>({
  openPopover: () => {},
  closePopover: () => {},
  currentEventId: null,
  setPreventClose: () => {},
  setOnOutsideClick: () => {},
  pointTo: () => {}
})

export const EventPopoverProvider = ({
  children
}: {
  children: React.ReactNode
}) => {
  const { resolvedThemeMode } = useAppSettings()
  const [state, setState] = useState<{
    eventId: string | null
    anchorEl: HTMLElement | null
    content: React.ReactNode
    preventClose: boolean
    onOutsideClick?: () => void
    lastPosition?: { x: number; y: number }
    animatePositionChanges: boolean
    repositionVersion: number
  }>({
    eventId: null,
    anchorEl: null,
    content: null,
    preventClose: false,
    onOutsideClick: undefined,
    lastPosition: undefined,
    animatePositionChanges: false,
    repositionVersion: 0
  })

  // Always-current ref so stable callbacks can read latest state without
  // being re-created on each state change.
  const stateRef = useRef(state)

  useEffect(() => {
    stateRef.current = state
  }, [state])

  // When openPopover switches from one event to another, a delayed closePopover
  // call from the old popup's ClickAwayListener (250 ms) would immediately close
  // the freshly-opened popup.  This flag blocks closePopover for a short window
  // after a redirect so that stale close calls are ignored.
  const redirectingRef = useRef(false)
  const highlightStyleElRef = useRef<HTMLStyleElement | null>(null)
  const highlightedAnchorRef = useRef<HTMLElement | null>(null)
  const anchorResolveTimerRef = useRef<number | null>(null)

  const clearAnchorResolveTimer = useCallback(() => {
    if (anchorResolveTimerRef.current !== null) {
      window.clearTimeout(anchorResolveTimerRef.current)
      anchorResolveTimerRef.current = null
    }
  }, [])

  const resolveAnchorWithRetry = useCallback(
    (eventId: string, animatePositionChanges: boolean) => {
      if (!eventId) return

      clearAnchorResolveTimer()

      let attempts = 0
      const maxAttempts = 10

      const tryResolve = () => {
        const resolvedElement = document.getElementById(eventId)
        if (resolvedElement) {
          setState((prev) => {
            if (!prev.content) return prev

            return {
              ...prev,
              eventId,
              anchorEl: resolvedElement,
              animatePositionChanges,
              repositionVersion: prev.repositionVersion + 1
            }
          })
          anchorResolveTimerRef.current = null
          return
        }

        attempts += 1
        if (attempts < maxAttempts) {
          anchorResolveTimerRef.current = window.setTimeout(tryResolve, 40)
        } else {
          anchorResolveTimerRef.current = null
        }
      }

      anchorResolveTimerRef.current = window.setTimeout(tryResolve, 0)
    },
    [clearAnchorResolveTimer]
  )

  useEffect(() => {
    return () => {
      clearAnchorResolveTimer()
    }
  }, [clearAnchorResolveTimer])

  useEffect(() => {
    if (highlightedAnchorRef.current !== state.anchorEl) {
      highlightedAnchorRef.current?.classList.remove(
        'event-popover-shadow-target'
      )
      highlightedAnchorRef.current = state.anchorEl
    }

    if (state.anchorEl && state.content) {
      state.anchorEl.classList.add('event-popover-shadow-target')
    } else {
      highlightedAnchorRef.current?.classList.remove(
        'event-popover-shadow-target'
      )
    }

    // Inject a global style rule keyed by the active event id. Because the
    // rule matches by `id` attribute (not by a DOM reference), the shadow
    // keeps applying even when FullCalendar tears down and recreates the
    // event element shortly after a new event is added via the calendar API.
    if (!highlightStyleElRef.current) {
      const styleEl = document.createElement('style')
      styleEl.setAttribute('data-event-popover-highlight', '')
      document.head.appendChild(styleEl)
      highlightStyleElRef.current = styleEl
    }

    const styleEl = highlightStyleElRef.current

    if (!state.eventId || !state.content) {
      styleEl.textContent = ''
      return
    }

    const escapedId =
      typeof CSS !== 'undefined' && CSS.escape
        ? CSS.escape(state.eventId)
        : state.eventId.replace(/(["\\])/g, '\\$1')

    const shadowValue =
      resolvedThemeMode === 'dark'
        ? `
          0 20px 46px rgb(105 105 105 / 62%), 0 10px 24px rgb(32 31 31 / 46%), 0 0 0 1px rgba(255, 255, 255, 0.14), 0 0 24px rgba(255, 255, 255, 0.22), 0 0 40px rgba(255, 255, 255, 0.08)
        `
        : `
          0 16px 36px rgba(0, 0, 0, 0.4),
          0 6px 16px rgba(0, 0, 0, 0.22),
          0 2px 6px rgba(0, 0, 0, 0.18)
        `

    styleEl.textContent = `
      [id="${escapedId}"] {
        position: relative !important;
        z-index: 12 !important;
        border-radius: 5px !important;
        box-shadow: ${shadowValue} !important;
        outline: none !important;
        transition: box-shadow 0.3s ease !important;
      }
    `
  }, [resolvedThemeMode, state.eventId, state.content, state.anchorEl])

  useEffect(() => {
    return () => {
      highlightedAnchorRef.current?.classList.remove(
        'event-popover-shadow-target'
      )
      highlightedAnchorRef.current = null

      if (highlightStyleElRef.current) {
        highlightStyleElRef.current.remove()
        highlightStyleElRef.current = null
      }
    }
  }, [])

  const openPopover = useCallback(
    (
      eventId: string,
      content: React.ReactNode,
      preventClose: boolean = false,
      onOutsideClick?: () => void,
      elementPointTo?: HTMLElement
    ) => {
      const element = elementPointTo ?? document.getElementById(eventId)
      Logger.orange('EventPopoverProvider:element', element)
      if (
        stateRef.current.anchorEl === element &&
        stateRef.current.eventId === eventId
      ) {
        return
      }
      // If a popup is already open we are switching targets — block the stale
      // delayed close that the old popup's ClickAwayListener will fire shortly.
      if (stateRef.current.anchorEl !== null) {
        redirectingRef.current = true
        setTimeout(() => {
          redirectingRef.current = false
        }, 400)
      }

      LocalStorage.set(LocalStorageKey.VISIBLE_EVENT, eventId)

      setState((prev) => ({
        eventId,
        anchorEl: element,
        content,
        preventClose,
        onOutsideClick,
        animatePositionChanges: false,
        repositionVersion: prev.repositionVersion + 1
      }))

      if (!element) {
        resolveAnchorWithRetry(eventId, false)
      }
    },
    [resolveAnchorWithRetry]
  )

  const closePopover = useCallback(
    (_withCache?: boolean) => {
      if (LocalStorage.get(LocalStorageKey.SHOULD_KEEP_POPUP) === '1') return
      if (redirectingRef.current) return
      clearAnchorResolveTimer()
      LocalStorage.remove(LocalStorageKey.VISIBLE_EVENT)
      setState((prev) => ({
        ...prev,
        anchorEl: _withCache ? prev.anchorEl : null,
        eventId: _withCache ? prev.eventId : null,
        content: null,
        onOutsideClick: undefined
      }))
    },
    [clearAnchorResolveTimer]
  )

  const setPreventClose = useCallback((value: boolean) => {
    setState((prev) => ({ ...prev, preventClose: value }))
  }, [])

  const setOnOutsideClick = useCallback((callback: () => void) => {
    setState((prev) => ({ ...prev, onOutsideClick: callback }))
  }, [])

  const pointTo = useCallback(
    (el: HTMLElement | null, eventId: string) => {
      const element = el ?? document.getElementById(eventId)
      if (!element) {
        resolveAnchorWithRetry(eventId, true)
        return
      }

      Logger.orange('EventPopoverProvider:pointTo', element)

      // Prefer the element's own DOM id so the shadow style selector (which
      // matches by `[id="…"]`) keeps working even when callers pass synthetic
      // labels like "dragged" after FullCalendar recreated the event node.
      const resolvedEventId = element.id || eventId

      setState((prev) => {
        if (!prev.content) return prev

        return {
          ...prev,
          eventId: resolvedEventId,
          anchorEl: element,
          animatePositionChanges: true,
          // Always bump so TFloatingPopover re-measures even when the
          // underlying DOM node is the same object (FullCalendar reuses the
          // event card element and just shifts it via CSS when only the
          // event time changes).
          repositionVersion: prev.repositionVersion + 1
        }
      })
    },
    [resolveAnchorWithRetry]
  )

  // All deps are stable useCallbacks → this object never changes reference
  const stableCallbacks = useMemo<PopoverCallbacksType>(
    () => ({
      openPopover,
      closePopover,
      setPreventClose,
      setOnOutsideClick,
      pointTo
    }),
    [openPopover, closePopover, setPreventClose, setOnOutsideClick, pointTo]
  )

  // Full reactive value — only currentEventId changes cause context consumers to re-render
  const value = useMemo<PopoverContextType>(
    () => ({ ...stableCallbacks, currentEventId: state.eventId }),
    [stableCallbacks, state.eventId]
  )

  Logger.orange('EventPopoverProvider:state', state)

  return (
    <EventPopoverCallbacksContext.Provider value={stableCallbacks}>
      <EventPopoverContext.Provider value={value}>
        {children}

        <TFloatingPopover
          surfaceLevel="primary"
          open={!!state.anchorEl}
          onClose={closePopover}
          anchorElement={state.anchorEl}
          content={
            <React.Fragment key={resolvedThemeMode}>
              {state.content}
            </React.Fragment>
          }
          defaultPlacement="left"
          fallbackPlacements={['left', 'right']}
          padding={0}
          viewportPadding={{ top: 32, right: 8, bottom: 40, left: 8 }}
          closeOnTouchOutside={false}
          borderRadius={'24px'}
          popupBorder={resolvedThemeMode === 'dark' ? undefined : 'none'}
          animatePositionChanges={state.animatePositionChanges}
          repositionVersion={state.repositionVersion}
          showArrow={false}
        />
      </EventPopoverContext.Provider>
    </EventPopoverCallbacksContext.Provider>
  )
}

// Full reactive hook — includes currentEventId.
// Use only when you need to READ the current open event id.
export const useEventPopover = () => {
  const context = useContext(EventPopoverContext)
  if (!context) {
    throw new Error('useEventPopover must be used within EventPopoverProvider')
  }
  return context
}

// Stable callbacks hook — never triggers re-renders when a popup opens/closes.
// Use in event cards and anywhere that only calls open/close/pointTo.
export const useEventPopoverCallbacks = (): PopoverCallbacksType => {
  return useContext(EventPopoverCallbacksContext)
}
